import {
  Activity,
  Bell,
  Circle,
  Gauge,
  LayoutDashboard,
  Map,
  Network,
  PieChart,
  Server,
  Settings,
  Share2,
  Stethoscope,
} from "lucide-react"
import type { SidebarGroup, SidebarLeaf, SidebarNestedGroup } from "../../utils/sidebarNav.ts"

export const SMARTOLT_PATHS = {
  root: "/smartolt",
  dashboard: "/smartolt/dashboard",
  unconfigured: "/smartolt/onus/unconfigured",
  configured: "/smartolt/onus",
  onuDetail: (onuId: string) => `/smartolt/onus/${encodeURIComponent(onuId)}`,
  terminated: "/smartolt/onus/terminated",
  billingSuspensions: "/smartolt/onus/billing-suspensions",
  olts: "/smartolt/olts",
  oltDetail: (oltId: string) => `/smartolt/olts/${encodeURIComponent(oltId)}`,
  alarms: "/smartolt/alarms",
  capacity: "/smartolt/capacity",
  maps: "/smartolt/maps",
  topology: "/smartolt/topology",
  xrtg: "/smartolt/xrtg",
  diagnostics: "/smartolt/diagnostics",
  reports: "/smartolt/reports",
  zones: "/smartolt/settings/zones",
  odbs: "/smartolt/settings/odbs",
  onuTypes: "/smartolt/settings/onu-types",
  speedProfiles: "/smartolt/settings/speed-profiles",
  settingsOlts: "/smartolt/settings/olts",
  system: "/smartolt/settings/system",
} as const

export type SmartOltSidebarOlt = {
  olt_id: string
  name: string
}

const onuGroup = (): SidebarNestedGroup => ({
  id: "smartolt-onus",
  title: "ONUs/ONTs",
  icon: Network,
  isDropdown: true,
  pathPrefix: "/smartolt/onus",
  subItems: [
    { title: "Unconfigured", path: SMARTOLT_PATHS.unconfigured, icon: Circle },
    { title: "Configured", path: SMARTOLT_PATHS.configured, icon: Circle, exact: true },
    { title: "Potentially Terminated", path: SMARTOLT_PATHS.terminated, icon: Circle },
    { title: "Billing Suspensions", path: SMARTOLT_PATHS.billingSuspensions, icon: Circle },
  ],
})

const settingsGroup = (): SidebarNestedGroup => ({
  id: "smartolt-settings",
  title: "Settings",
  icon: Settings,
  isDropdown: true,
  pathPrefix: "/smartolt/settings",
  subItems: [
    { title: "Zones", path: SMARTOLT_PATHS.zones, icon: Circle },
    { title: "ODB/ODF/Closures", path: SMARTOLT_PATHS.odbs, icon: Circle },
    { title: "ONU types", path: SMARTOLT_PATHS.onuTypes, icon: Circle },
    { title: "Speed Profiles", path: SMARTOLT_PATHS.speedProfiles, icon: Circle },
    { title: "OLTs", path: SMARTOLT_PATHS.settingsOlts, icon: Circle },
    { title: "System config", path: SMARTOLT_PATHS.system, icon: Circle },
  ],
})

export function oltSidebarLeaves(olts: SmartOltSidebarOlt[]): SidebarLeaf[] {
  if (olts.length === 0) {
    return [{ title: "OLTs", path: SMARTOLT_PATHS.olts, icon: Server }]
  }
  return olts.map((olt) => ({
    title: olt.name || `OLT ${olt.olt_id}`,
    path: SMARTOLT_PATHS.oltDetail(olt.olt_id),
    icon: Server,
  }))
}

export function buildSmartOltMenuGroup(olts: SmartOltSidebarOlt[] = []): SidebarGroup {
  const oltGroup: SidebarNestedGroup = {
    id: "smartolt-olts",
    title: "OLTs",
    icon: Server,
    isDropdown: true,
    pathPrefix: "/smartolt/olts",
    subItems: oltSidebarLeaves(olts),
  }

  return {
    id: "smartolt",
    title: "Networks",
    icon: Server,
    isDropdown: true,
    pathPrefix: SMARTOLT_PATHS.root,
    subItems: [
      { title: "Dashboard", path: SMARTOLT_PATHS.dashboard, icon: LayoutDashboard, exact: true },
      onuGroup(),
      oltGroup,
      { title: "Alarms", path: SMARTOLT_PATHS.alarms, icon: Bell },
      { title: "Capacity", path: SMARTOLT_PATHS.capacity, icon: Gauge },
      { title: "Maps", path: SMARTOLT_PATHS.maps, icon: Map },
      { title: "Live Topology", path: SMARTOLT_PATHS.topology, icon: Share2 },
      { title: "XRTG", path: SMARTOLT_PATHS.xrtg, icon: Activity },
      { title: "Diagnostics", path: SMARTOLT_PATHS.diagnostics, icon: Stethoscope },
      { title: "Reports", path: SMARTOLT_PATHS.reports, icon: PieChart },
      settingsGroup(),
    ],
  }
}
